import { useCallback, useEffect, useState } from 'react';
import type { Dimension, EntryDetail as EntryDetailModel, LevelDescriptor } from '../../shared/types.js';
import type { EvidenceKind, Level, Scope, Status } from '../../shared/levels.js';
import { EVIDENCE_KINDS, LEVELS, STATUSES } from '../../shared/levels.js';
import { api } from '../api/client.js';

interface Props {
  entryId: string;
  dimensions: Dimension[];
  descriptors: LevelDescriptor[];
  onBack: () => void;
  onChanged: () => void;
}

interface Form {
  title: string;
  occurred_on: string;
  summary: string;
  impact: string;
  recognition: string;
  scope: Scope;
  project: string;
  status: Status;
}

function toForm(d: EntryDetailModel): Form {
  return {
    title: d.title,
    occurred_on: d.occurred_on,
    summary: d.summary ?? '',
    impact: d.impact ?? '',
    recognition: d.recognition ?? '',
    scope: d.scope,
    project: d.project ?? '',
    status: d.status,
  };
}

export function EntryDetail({ entryId, dimensions, descriptors, onBack, onChanged }: Props) {
  const [detail, setDetail] = useState<EntryDetailModel | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Tag-add controls
  const [tagDim, setTagDim] = useState('');
  const [tagLevel, setTagLevel] = useState<Level>('T3');
  const [tagRationale, setTagRationale] = useState('');

  // Evidence-add controls
  const [evKind, setEvKind] = useState<EvidenceKind>('link');
  const [evLabel, setEvLabel] = useState('');
  const [evUrl, setEvUrl] = useState('');

  const reload = useCallback(async () => {
    try {
      const d = await api.getEntry(entryId);
      setDetail(d);
      setForm(toForm(d));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [entryId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (error) return <ErrorBack message={error} onBack={onBack} />;
  if (!detail || !form) return <p className="empty">Loading…</p>;

  const dimName = (id: string) => dimensions.find((d) => d.id === id)?.name ?? id;
  const descriptorFor = (dimId: string, level: Level) =>
    descriptors.find((x) => x.dimension_id === dimId && x.level === level)?.descriptor ?? null;

  function field<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateEntry(entryId, {
        title: form.title,
        occurred_on: form.occurred_on,
        summary: form.summary,
        impact: form.impact,
        recognition: form.recognition,
        scope: form.scope,
        project: form.project,
        status: form.status,
      });
      onChanged();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm('Delete this entry and all its tags and evidence?')) return;
    try {
      await api.deleteEntry(entryId);
      onChanged();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  async function addTag() {
    if (!tagDim) return;
    try {
      await api.upsertEntryDimension(entryId, tagDim, {
        demonstrated_level: tagLevel,
        rationale: tagRationale,
      });
      setTagRationale('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to tag');
    }
  }

  async function addEvidence() {
    if (!evLabel.trim() && !evUrl.trim()) return;
    try {
      await api.createEvidence(entryId, { kind: evKind, label: evLabel, url: evUrl });
      setEvLabel('');
      setEvUrl('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add evidence');
    }
  }

  const tagHint = tagDim ? descriptorFor(tagDim, tagLevel) : null;

  return (
    <section className="detail">
      <div className="detail-top">
        <button type="button" className="ghost" onClick={onBack}>
          ← Back
        </button>
        <div className="detail-actions">
          <button type="button" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="danger" onClick={() => void remove()}>
            Delete
          </button>
        </div>
      </div>

      {/* Core fields — only the title is required; the rest enrich over time. */}
      <label className="fld">
        Title
        <input value={form.title} onChange={(e) => field('title', e.target.value)} />
      </label>
      <div className="fld-row">
        <label className="fld">
          Date
          <input type="date" value={form.occurred_on} onChange={(e) => field('occurred_on', e.target.value)} />
        </label>
        <label className="fld">
          Status
          <select value={form.status} onChange={(e) => field('status', e.target.value as Status)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          Scope
          <select value={form.scope} onChange={(e) => field('scope', e.target.value as Scope)}>
            <option value="in_role">In role</option>
            <option value="stretch">Above band</option>
          </select>
        </label>
        <label className="fld">
          Project
          <input value={form.project} onChange={(e) => field('project', e.target.value)} />
        </label>
      </div>
      <label className="fld">
        Summary — what happened
        <textarea value={form.summary} rows={3} onChange={(e) => field('summary', e.target.value)} />
      </label>
      <label className="fld">
        Impact — the so-what, quantified
        <textarea value={form.impact} rows={2} onChange={(e) => field('impact', e.target.value)} />
      </label>
      <label className="fld">
        Recognition — who noticed
        <textarea value={form.recognition} rows={2} onChange={(e) => field('recognition', e.target.value)} />
      </label>

      {/* Dimension tags: the heart of the model. */}
      <h3>Dimensions &amp; levels</h3>
      {detail.dimensions.length === 0 && <p className="muted">Not yet tagged to any dimension.</p>}
      <ul className="tag-list">
        {detail.dimensions.map((t) => (
          <li key={t.dimension_id} className="tag-row">
            <span className="tag-name">{dimName(t.dimension_id)}</span>
            <span className="badge level">{t.demonstrated_level}</span>
            <span className="tag-rationale">{t.rationale ?? <em className="muted">no rationale</em>}</span>
            <button
              type="button"
              className="ghost small"
              onClick={() => void api.deleteEntryDimension(entryId, t.dimension_id).then(reload)}
            >
              remove
            </button>
          </li>
        ))}
      </ul>
      <div className="tag-add">
        <select value={tagDim} onChange={(e) => setTagDim(e.target.value)} aria-label="Dimension">
          <option value="">Add dimension…</option>
          {dimensions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select value={tagLevel} onChange={(e) => setTagLevel(e.target.value as Level)} aria-label="Level">
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <input
          placeholder="One-line rationale (why this level)"
          value={tagRationale}
          onChange={(e) => setTagRationale(e.target.value)}
        />
        <button type="button" onClick={() => void addTag()} disabled={!tagDim}>
          Tag
        </button>
      </div>
      {tagHint && <p className="hint">{tagLevel}: {tagHint}</p>}

      {/* Evidence */}
      <h3>Evidence</h3>
      {detail.evidence.length === 0 && <p className="muted">No proof links yet.</p>}
      <ul className="evidence-list">
        {detail.evidence.map((ev) => (
          <li key={ev.id} className="evidence-row">
            <span className="badge kind">{ev.kind}</span>
            {ev.url ? (
              <a href={ev.url} target="_blank" rel="noreferrer">
                {ev.label || ev.url}
              </a>
            ) : (
              <span>{ev.label}</span>
            )}
            <button
              type="button"
              className="ghost small"
              onClick={() => void api.deleteEvidence(ev.id).then(reload)}
            >
              remove
            </button>
          </li>
        ))}
      </ul>
      <div className="evidence-add">
        <select value={evKind} onChange={(e) => setEvKind(e.target.value as EvidenceKind)} aria-label="Evidence kind">
          {EVIDENCE_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input placeholder="Label" value={evLabel} onChange={(e) => setEvLabel(e.target.value)} />
        <input placeholder="URL" value={evUrl} onChange={(e) => setEvUrl(e.target.value)} />
        <button type="button" onClick={() => void addEvidence()}>
          Add
        </button>
      </div>
    </section>
  );
}

function ErrorBack({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <section className="detail">
      <button type="button" className="ghost" onClick={onBack}>
        ← Back
      </button>
      <p className="error">{message}</p>
    </section>
  );
}
